import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchCurrentUser, UserInfo, SectionPermissions } from 'src/services/api';
import { getRoleOverride, setRoleOverride as persistRoleOverride } from 'src/services/headers';
import { resetSignInAttempts } from 'src/services/session';

type Section = keyof SectionPermissions;

interface UserContextType {
    user: UserInfo | null;
    loading: boolean;
    /** Real admin status of the logged-in user, independent of any active role override. */
    isAdmin: boolean;
    hasRole: (...roles: string[]) => boolean;
    canRead: (section: Section) => boolean;
    canWrite: (section: Section) => boolean;
    /**
     * Whether the current user may edit/delete a specific record. Requires write access to
     * the section AND either a full-edit role (admin/super-author) or ownership of the record
     * (createdBy matches the user's alias). Mirrors the backend ownership enforcement.
     */
    canModify: (section: Section, createdBy?: string) => boolean;
    roleOverride: string | null;
    setRoleOverride: (role: string | null) => void;
}

const UserContext = createContext<UserContextType>({
    user: null,
    loading: true,
    isAdmin: false,
    hasRole: () => false,
    canRead: () => false,
    canWrite: () => false,
    canModify: () => false,
    roleOverride: null,
    setRoleOverride: () => {},
});

const isAdminUser = (user: UserInfo | null): boolean =>
    !!user?.permissions?.some(pm => pm.toLowerCase() === 'admin');

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserInfo | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [roleOverride, setRoleOverrideState] = useState<string | null>(getRoleOverride());

    const loadUser = useCallback(async () => {
        setLoading(true);
        try {
            // Effective identity (respects any active role override).
            let effective = await fetchCurrentUser();

            // Real admin status: when a role override is active, the effective identity
            // reflects the impersonated role, so fetch the un-overridden identity to know
            // whether the *real* user is an admin. This keeps the role switcher visible to
            // admins while they impersonate another role.
            if (getRoleOverride()) {
                const real = await fetchCurrentUser(false);

                // Only act on a SUCCESSFUL real-identity fetch. fetchCurrentUser returns null on
                // error (it does not throw), so without this guard a transient failure of this
                // second fetch would set `effective = null` below — nulling an already-known
                // authenticated user (which now triggers an SSO redirect) and wrongly clearing the
                // override. On failure we keep the effective identity and override intact.
                if (real) {
                    const realIsAdmin = isAdminUser(real);

                    // Cleanup: only real admins may use the role override. If a non-admin
                    // has a lingering override in localStorage (e.g. stale value, shared
                    // machine), clear it and fall back to their real identity so the UI
                    // never shows them impersonating a higher role.
                    if (!realIsAdmin) {
                        persistRoleOverride(null);
                        setRoleOverrideState(null);
                        effective = real;
                    }

                    setIsAdmin(realIsAdmin);
                } else {
                    // Real-identity fetch failed; derive admin status from the effective identity
                    // as a best-effort fallback rather than downgrading/clearing state.
                    setIsAdmin(isAdminUser(effective));
                }
            } else {
                setIsAdmin(isAdminUser(effective));
            }

            // A user was successfully established — clear the sign-in attempt counter so a
            // future lapse starts from a clean slate (and doesn't inherit a stale count).
            if (effective) resetSignInAttempts();

            setUser(effective);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const setRoleOverride = (role: string | null) => {
        persistRoleOverride(role);
        setRoleOverrideState(role);
        // Re-fetch user with new override
        loadUser();
    };

    const hasRole = (...roles: string[]) => {
        if (!user?.permissions) return false;
        return user.permissions.some(p =>
            roles.map(r => r.toLowerCase()).includes(p.toLowerCase())
        );
    };

    const canRead = (section: Section): boolean => {
        return user?.sectionPermissions?.[section]?.read ?? false;
    };

    const canWrite = (section: Section): boolean => {
        return user?.sectionPermissions?.[section]?.write ?? false;
    };

    const canModify = (section: Section, createdBy?: string): boolean => {
        if (!canWrite(section)) return false;
        // Admins and super-authors may modify any record.
        if (hasRole('admin', 'super-author')) return true;
        // Everyone else may only modify records they created.
        return !!createdBy && !!user?.alias && createdBy === user.alias;
    };

    return (
        <UserContext.Provider value={{ user, loading, isAdmin, hasRole, canRead, canWrite, canModify, roleOverride, setRoleOverride }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
